import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { X, Search, ChevronDown, ChevronUp } from 'lucide-react'
import { apiClient } from '@api'
import './CertificateSelector.css'

/**
 * CertificateSelector Component
 *
 * Compact multi-select component for selecting compliance certificates
 *
 * Features:
 * - Selected items displayed as tags at the top with remove option
 * - Collapsible certificate list
 * - Search/filter certificates
 * - Multi-select with checkboxes
 * - Compact, modern UI design
 * - Loading and error states
 *
 * @param {Array} value - Array of selected certificate IDs
 * @param {Function} onChange - Callback when selection changes
 * @param {Boolean} disabled - Disable the selector
 * @param {String} error - Error message to display
 */
const CertificateSelector = ({ value = [], onChange, disabled = false, error = null }) => {
	const [certificates, setCertificates] = useState([])
	const [loading, setLoading] = useState(true)
	const [searchTerm, setSearchTerm] = useState('')
	const [fetchError, setFetchError] = useState(null)
	const [isExpanded, setIsExpanded] = useState(true)

	// Fetch certificates from API
	useEffect(() => {
		const fetchCertificates = async () => {
			try {
				setLoading(true)
				setFetchError(null)

				const response = await apiClient.get('/certificates?status=active&limit=100')

				if (response.data && Array.isArray(response.data)) {
					// Convert certificate IDs to integers for consistent comparison
					const normalizedCertificates = response.data.map((cert) => ({
						...cert,
						id: parseInt(cert.id, 10),
					}))
					setCertificates(normalizedCertificates)
				} else {
					throw new Error('Invalid response format')
				}
			} catch (err) {
				console.error('Failed to fetch certificates:', err)
				setFetchError(err.message || 'Failed to load certificates')
			} finally {
				setLoading(false)
			}
		}

		fetchCertificates()
	}, [])

	// Get selected certificates with their details
	const selectedCertificates = useMemo(() => {
		return certificates.filter((cert) => value.includes(cert.id))
	}, [certificates, value])

	// Filter certificates based on search term (excluding already selected)
	const filteredCertificates = useMemo(() => {
		return certificates.filter(
			(cert) =>
				cert.title.toLowerCase().includes(searchTerm.toLowerCase())
		)
	}, [certificates, searchTerm])

	// Handle checkbox change
	const handleToggle = (certId) => {
		if (disabled) return

		const newValue = value.includes(certId)
			? value.filter((id) => id !== certId)
			: [...value, certId]

		onChange(newValue)
	}

	// Handle removing a selected item
	const handleRemove = (certId) => {
		if (disabled) return
		onChange(value.filter((id) => id !== certId))
	}

	// Handle select all visible (non-selected)
	const handleSelectAll = () => {
		if (disabled) return
		const visibleIds = filteredCertificates.map((cert) => cert.id)
		const allSelected = visibleIds.every((id) => value.includes(id))

		if (allSelected) {
			// Deselect all visible
			onChange(value.filter((id) => !visibleIds.includes(id)))
		} else {
			// Select all visible
			const newValue = [...new Set([...value, ...visibleIds])]
			onChange(newValue)
		}
	}

	// Handle clear all
	const handleClearAll = () => {
		if (disabled) return
		onChange([])
	}

	const selectedCount = value.length
	const allVisibleSelected = filteredCertificates.length > 0 &&
		filteredCertificates.every((cert) => value.includes(cert.id))

	return (
		<div className={`cert-select ${disabled ? 'cert-select--disabled' : ''}`}>
			{/* Selected Items as Tags */}
			{selectedCount > 0 && (
				<div className="cert-select__selected">
					<div className="cert-select__selected-header">
						<span className="cert-select__selected-label">
							Selected ({selectedCount})
						</span>
						<button
							type="button"
							className="cert-select__clear-all"
							onClick={handleClearAll}
							disabled={disabled}
						>
							Clear All
						</button>
					</div>
					<div className="cert-select__tags">
						{selectedCertificates.map((cert) => (
							<div key={cert.id} className="cert-select__tag">
								<span className="cert-select__tag-text">{cert.title}</span>
								<button
									type="button"
									className="cert-select__tag-remove"
									onClick={() => handleRemove(cert.id)}
									disabled={disabled}
									aria-label={`Remove ${cert.title}`}
								>
									<X size={12} />
								</button>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Collapsible List Header */}
			<div className="cert-select__header">
				<button
					type="button"
					className="cert-select__toggle"
					onClick={() => setIsExpanded(!isExpanded)}
					disabled={loading}
				>
					{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					<span>
						{isExpanded ? 'Hide' : 'Show'} Available Certificates
						{!loading && ` (${certificates.length})`}
					</span>
				</button>
			</div>

			{/* Expandable Content */}
			{isExpanded && (
				<div className="cert-select__content">
					{/* Search and Select All Row */}
					<div className="cert-select__toolbar">
						<div className="cert-select__search">
							<Search size={14} className="cert-select__search-icon" />
							<input
								type="text"
								className="cert-select__search-input"
								placeholder="Search certificates..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								disabled={disabled || loading}
							/>
							{searchTerm && (
								<button
									type="button"
									className="cert-select__search-clear"
									onClick={() => setSearchTerm('')}
									disabled={disabled || loading}
								>
									<X size={12} />
								</button>
							)}
						</div>

						{!loading && filteredCertificates.length > 0 && (
							<button
								type="button"
								className="cert-select__select-all"
								onClick={handleSelectAll}
								disabled={disabled}
							>
								{allVisibleSelected ? 'Deselect All' : 'Select All'}
							</button>
						)}
					</div>

					{/* Certificate List */}
					<div className="cert-select__list">
						{loading && (
							<div className="cert-select__loading">
								<div className="cert-select__spinner" />
								<span>Loading...</span>
							</div>
						)}

						{fetchError && (
							<div className="cert-select__error">{fetchError}</div>
						)}

						{!loading && !fetchError && filteredCertificates.length === 0 && (
							<div className="cert-select__empty">
								{searchTerm
									? `No results for "${searchTerm}"`
									: 'No certificates available'}
							</div>
						)}

						{!loading &&
							!fetchError &&
							filteredCertificates.map((cert) => {
								const isSelected = value.includes(cert.id)

								return (
									<label
										key={cert.id}
										className={`cert-select__item ${isSelected ? 'cert-select__item--selected' : ''}`}
									>
										<input
											type="checkbox"
											className="cert-select__checkbox"
											checked={isSelected}
											onChange={() => handleToggle(cert.id)}
											disabled={disabled}
										/>
										<span className="cert-select__item-text">{cert.title}</span>
									</label>
								)
							})}
					</div>
				</div>
			)}

			{/* Error Message */}
			{error && <div className="cert-select__validation-error">{error}</div>}
		</div>
	)
}

CertificateSelector.propTypes = {
	value: PropTypes.arrayOf(PropTypes.number),
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	error: PropTypes.string,
}

export default CertificateSelector
